import { trackAnalytics } from "utils/analytics";
import Alert, { AlertState } from 'Components/Organisms/Alert';
import CouponChangeDialog from 'App/Coupons/CouponChange/CouponChangeDialog';
import useCollection from 'CustomHooks/useCollection';
import React, {
  createContext,
  useCallback,
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from 'react';
import firebase, { db } from 'utils/firebase';
import { asyncVoid, dateHelper, noop } from 'utils/helper';
import logFirestoreError from 'utils/logFirestoreError';
import { Products, Translations } from 'utils/types';
import { AuthContext } from './AuthContext';
import { Notification } from 'utils/types';
import Toasts from 'Components/Organisms/Toasts';
import { StylesProvider } from '@material-ui/core';
import SignatureContextProvider from './SignatureContext';
import useNotifications, { NotificationV02 } from 'CustomHooks/useNotification';
import useDocument from 'CustomHooks/useDocument';
import useLoadedFor from 'CustomHooks/useLoadedFor';
import useSecurityPlusPolicy from 'CustomHooks/useSecurityPlusPolicy';
import * as DocumentTypes from 'types/shared/documents';
import { StripeSubscriptionDocument } from 'types/shared/documents/restaurants/credentials';
import TextHelper, { TextHelperState } from 'Components/Organisms/TextHelper';
import LinkGenerator from 'Components/Organisms/LinkGenerator';
import {
  AppId,
  SecurityPlus,
  AppSettings,
} from 'types/shared/documents/restaurants';
import PinAlert from 'Components/Organisms/PinAlert';
import SecurityActivityProvider from './SecurityActivityContext';
import { randomId } from 'utils/securityActivity/events';
import useLocalStorageState from 'CustomHooks/useLocalStorageState';
import { AddOnOrder } from 'App/Experiences/types/addOnOrder';
import { NotificationV03 } from 'types/shared/helper/notification';
import ExperimentalFlagsModal, {
  ExperimentalFlags,
  GlobalFlags,
} from 'App/ReservationBook/Components/ExperimentalFlags';
import { SiaState } from 'types/sia';
import usePublishedCustomForms from 'CustomHooks/usePublishedCustomForms';
import { buildCustomDataCatalog, CustomDataCatalog, EMPTY_CUSTOM_DATA_CATALOG } from 'utils/customDataPresentation';

type ReservationSettings =
  DocumentTypes.Restaurant.Settings.ReservationSettingsDocument;

export type PinSession = { sessionId: string };

export type PinAuth = {
  onSubmit: (signature: string, session?: PinSession) => void | Promise<void>;
  /** `scope_changed`: login, restaurant, PINs or rights changed while it was open. */
  onClose?: (reason?: 'scope_changed') => void;
  onWrongPin?: (initials: string) => void;
  signatures: string[];
  type: string;
};

type PinCache = {
  scope: string;
  pins: Record<string, { expiresAt: number; sessionId: string }>;
};

export enum Severity {
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
  INFO = 'info',
}

export type Toast = {
  id: number;
  severity: Severity;
  description: string;
  descriptionTranslation?: Translations;
  onRevert?: VoidFunction;
};

type Props = {
  restaurantId: null | string;
  setrestaurantId: Function;
  children?: React.ReactNode;
};

type Context = {
  products: Products[];
  switchRestaurant: Function;
  restaurantId: null | string;
  alert: React.Dispatch<React.SetStateAction<AlertState | null>>;
  pinAuth: (prompt: null | PinAuth) => void;

  // notifications: Notification[];
  newToast: (
    description: string,
    severity: Severity,
    /** Namespace of the description; `null` = already translated, shown as is. */
    translation?: Translations,
    onRevert?: VoidFunction,
  ) => void;
  restaurantName: string;
  restaurantEmail: string;
  restaurantPhone: string;
  lightReservation: boolean;
  notifications: NotificationV02[];
  notificationsLoading: boolean;
  markNotificationAsSeen: (id: string) => Promise<void>;
  getAllNotifications: () => Promise<NotificationV02[] | void>;
  markAllNotificationsAsSeen: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  reservationSettings: null | ReservationSettings;
  hasSia: boolean;
  subscriptions: null | StripeSubscriptionDocument['subscriptionItems'];
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  textHelperState: null | TextHelperState;
  initTextHelper: React.Dispatch<React.SetStateAction<TextHelperState | null>>;
  apps: AppId[];
  setapps: React.Dispatch<
    React.SetStateAction<DocumentTypes.Restaurant.AppId[]>
  >;
  /** The restaurant `apps` was loaded for; differs from restaurantId while a switch loads. */
  appsRestaurantId: string | null;
  securityPlus: null | AppSettings<SecurityPlus>;
  securityPlusLoaded: boolean;
  storedPins: Record<string, number>;
  /** The PIN session (one PIN entry) behind each cached PIN. */
  storedPinSessions?: Record<string, string>;
  filteredNotifications: (NotificationV03 & {
    id: string;
  })[];
  notificationsV03Open: boolean;
  setnotificationsV03Open: React.Dispatch<React.SetStateAction<boolean>>;
  supportOpen: boolean;
  setSupportOpen: React.Dispatch<React.SetStateAction<boolean>>;
  notificationCategories: Record<string, string[]>;
  experimentalFlags: {
    ref: any;
    data: ExperimentalFlags | null;
    loading: boolean;
    error: string | null;
    exists: boolean;
  };
  globalFlags: {
    ref: any;
    data: GlobalFlags | null;
    loading: boolean;
    error: string | null;
    exists: boolean;
  };
  hasSiaV02: boolean;
  siaState: {
    ref: any;
    data: SiaState | null;
    loading: boolean;
    error: string | null;
    exists: boolean;
  };
  deleted: boolean;
  deletedAt: number | null;
  projectId: string;
  /** restaurantData.dashboardProductLayout of the current restaurant, unparsed. */
  dashboardProductLayout: { raw: unknown; loaded: boolean };
  customDataCatalog: CustomDataCatalog;
};

// Dashboard-only preference; the shared restaurantData type stays untouched.
type RestaurantDataWithDashboardLayout =
  DocumentTypes.RestaurantDataDocument & { dashboardProductLayout?: unknown };

export const RestaurantContext = createContext<Context>({
  products: [],
  restaurantId: null,
  switchRestaurant: noop,
  alert: noop,
  pinAuth: noop,
  newToast: noop,
  restaurantName: '',
  restaurantEmail: '',
  restaurantPhone: '',
  lightReservation: false,
  supportOpen: false,
  setSupportOpen: noop,
  notifications: [],
  notificationsLoading: false,
  getAllNotifications: asyncVoid,
  markAllNotificationsAsSeen: asyncVoid,
  markNotificationAsSeen: asyncVoid,
  deleteNotification: asyncVoid,
  reservationSettings: null,
  hasSia: false,
  subscriptions: null,
  search: '',
  setSearch: noop,
  textHelperState: null,
  initTextHelper: noop,
  apps: [],
  setapps: noop,
  appsRestaurantId: null,
  securityPlus: null,
  securityPlusLoaded: true,
  storedPins: {},
  storedPinSessions: {},
  filteredNotifications: [],
  notificationsV03Open: true,
  setnotificationsV03Open: noop,
  notificationCategories: {},
  experimentalFlags: {
    ref: null,
    data: null,
    loading: false,
    error: null,
    exists: false,
  },
  globalFlags: {
    ref: null,
    data: null,
    loading: false,
    error: null,
    exists: false,
  },
  hasSiaV02: false,
  siaState: {
    ref: null,
    data: null,
    loading: false,
    error: null,
    exists: false,
  },
  deleted: false,
  deletedAt: null,
  projectId: '',
  dashboardProductLayout: { raw: undefined, loaded: true },
  customDataCatalog: EMPTY_CUSTOM_DATA_CATALOG,
});

/*
    Everything needed for Restaurant
*/

type NotificationCollection = {
  ref: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>;
  data: Notification[];
  loading: boolean;
  error: null | string;
};

const RestaurantContextProvider = ({
  restaurantId,
  setrestaurantId,
  children,
}: Props) => {
  const { user, projectId } = useContext(AuthContext);
  const publishedCustomForms = usePublishedCustomForms(restaurantId, user?.uid);
  const customDataCatalog = useMemo(() => buildCustomDataCatalog(publishedCustomForms), [publishedCustomForms]);

  const [alertState, alert] = useState<null | AlertState>(null);
  const [pinPrompt, setPinPrompt] = useState<null | { prompt: PinAuth; scope: string }>(null);
  const pinAuthState = pinPrompt?.prompt ?? null;

  const {
    securityPlusDoc,
    experimentalFlags,
    loaded: securityPlusLoaded,
  } = useSecurityPlusPolicy(restaurantId);

  const [globalFlags] = useDocument<GlobalFlags>(
    `restaurants/${restaurantId}/other`,
    'globalFlags',
  );

  // A cached PIN is only valid for the login, restaurant, PINs and rights it
  // was entered under; any change of those asks for the PIN again.
  const pinScope = useMemo(
    () =>
      JSON.stringify([
        user?.uid ?? null,
        restaurantId,
        securityPlusDoc.data?.active ?? null,
        securityPlusDoc.data?.settings ?? null,
        experimentalFlags.data?.disableSecurityForAdmins ?? null,
      ]),
    [
      user?.uid,
      restaurantId,
      securityPlusDoc.data,
      experimentalFlags.data?.disableSecurityForAdmins,
    ],
  );
  const [pinCache, setPinCache] = useState<PinCache>({ scope: '', pins: {} });

  // An open PIN prompt belongs to the scope it was opened in: after a change it
  // is closed as cancelled and its old callback never runs.
  const pinScopeRef = useRef(pinScope);
  pinScopeRef.current = pinScope;
  const pinAuth = useCallback(
    (prompt: null | PinAuth) =>
      setPinPrompt(prompt ? { prompt, scope: pinScopeRef.current } : null),
    [],
  );
  useEffect(() => {
    if (pinPrompt && pinPrompt.scope !== pinScope) {
      pinPrompt.prompt.onClose?.('scope_changed');
      setPinPrompt(null);
    }
  }, [pinPrompt, pinScope]);
  const { storedPins, storedPinSessions } = useMemo(() => {
    const pins = pinCache.scope === pinScope ? Object.entries(pinCache.pins) : [];
    return {
      storedPins: Object.fromEntries(pins.map(([pin, p]) => [pin, p.expiresAt])),
      storedPinSessions: Object.fromEntries(pins.map(([pin, p]) => [pin, p.sessionId])),
    };
  }, [pinCache, pinScope]);

  const [toasts, setToasts] = useState<null | Toast[]>(null);
  const [search, setSearch] = useState<string>('');

  const [restaurantName, setRestaurantName] = useState<string>('');
  const [restaurantPhone, setRestaurantPhone] = useState<string>('');
  const [restaurantEmail, setRestaurantEmail] = useState<string>('');
  const [deleted, setDeleted] = useState<boolean>(false);
  const [deletedAt, setDeletedAt] = useState<number | null>(null);

  useEffect(() => {
    window.document.title = [restaurantName, 'Gastronaut Dashboard']
      .filter(Boolean)
      .join(' - ');
  }, [restaurantName]);

  const [reservationSettings] = useDocument<ReservationSettings>(
    `restaurants/${restaurantId}/settings`,
    'reservationsV02',
  );

  // credentials/* is only readable by the tablet role and Gastronaut admins, so
  // this every-page read is denied for regular dashboard users by design. Silence
  // the expected permission-denied instead of spamming the console on every page.
  const [subscriptionDocument] = useDocument<StripeSubscriptionDocument>(
    `restaurants/${restaurantId}/credentials`,
    'stripeSubscription',
    false /* editDocOnSet */,
    true /* silenceExpectedPermissionErrors */,
  );

  const subscriptions = subscriptionDocument?.data?.subscriptionItems ?? null;

  const [reservationSettingsDraft] = useDocument<ReservationSettings>(
    `restaurants/${restaurantId}/settingDrafts`,
    'reservationsV02',
  );

  const [products, setproducts] = useState<Products[]>([]);
  const [apps, setapps] = useState<AppId[]>([]);
  const [appsRestaurantId, setappsRestaurantId] = useState<string | null>(null);
  // Only the newest load may set the app list: a late answer for the previous
  // restaurant must not be taken as the current restaurant's apps.
  const appsRequestedFor = useRef<string | null>(null);

  const switchRestaurant = (restaurantId: string) => {
    if (user?.data?.restaurants?.find((r) => r.id === restaurantId)) {
      setrestaurantId(restaurantId);
      loadData(restaurantId);
    }
  };

  const loadData = async (restaurantId: string) => {
    appsRequestedFor.current = restaurantId;
    try {
      const ref = db.collection('restaurantData').doc(restaurantId);
      const settingsRef = db
        .collection(`restaurants/${restaurantId}/settings`)
        .doc('general');

      const [doc, settingsDoc] = await Promise.all([
        ref.get(),
        settingsRef.get(),
      ]);

      if (!doc.exists) {
        throw new Error('No Restaurant with this Id ' + restaurantId);
      }

      const restaurantData = doc?.data();

      if (restaurantData) {
        const { productsOwned = [] } = restaurantData as {
          productsOwned: Products[];
        };
        setproducts(productsOwned || []);
        setRestaurantName(restaurantData.name ?? '');
        setRestaurantPhone(restaurantData.customerInfo.phoneNumber ?? '');
        if (appsRequestedFor.current === restaurantId) {
          setapps(restaurantData?.apps ?? []);
          setappsRestaurantId(restaurantId);
        }
        setRestaurantEmail(settingsDoc?.data()?.email ?? '');
        setDeleted(!!restaurantData?.deleted);
        setDeletedAt(
          typeof restaurantData?.deletedAt === 'number'
            ? restaurantData.deletedAt
            : null,
        );

        if (!restaurantData?.apps) {
          // @TODO run function to check apps
        }
      }

      console.log({ restaurantData });
      return;
    } catch (error) {
      logFirestoreError(
        'RestaurantContext.loadData',
        `restaurantData/${restaurantId}, restaurants/${restaurantId}/settings/general`,
        error,
      );
      setrestaurantId(null);
    }
  };

  useEffect(() => {
    if (!restaurantId) {
      setproducts([]);
      appsRequestedFor.current = null;
      setapps([]);
      setappsRestaurantId(null);
      setDeleted(false);
      setDeletedAt(null);
    } else {
      loadData(restaurantId);
    }
    // eslint-disable-next-line
  }, [restaurantId]);

  const [restaurantDataLive] = useDocument<RestaurantDataWithDashboardLayout>(
    'restaurantData',
    restaurantId,
  );

  // Never the previous restaurant's layout right after a switch.
  const restaurantDataLoaded = useLoadedFor(
    `${restaurantId}`,
    restaurantDataLive.loading,
  );
  const storedProductLayout = restaurantDataLive.data?.dashboardProductLayout;
  const dashboardProductLayout = useMemo(
    () => ({
      raw: restaurantDataLoaded ? storedProductLayout : undefined,
      loaded: restaurantDataLoaded,
    }),
    [restaurantDataLoaded, storedProductLayout],
  );

  useEffect(() => {
    if (!restaurantId) return;
    setDeleted(!!restaurantDataLive.data?.deleted);
    setDeletedAt(
      typeof restaurantDataLive.data?.deletedAt === 'number'
        ? restaurantDataLive.data.deletedAt
        : null,
    );
  }, [
    restaurantId,
    restaurantDataLive.data?.deleted,
    restaurantDataLive.data?.deletedAt,
  ]);

  const {
    notifications,
    notificationsLoading,
    getAllNotifications,
    markAllNotificationsAsSeen,
    markNotificationAsSeen,
    deleteNotification,
  } = useNotifications(restaurantId ?? '');

  const newToast = (
    description: string,
    severity: Severity = Severity.INFO,
    translation?: Translations,
    onRevert?: VoidFunction,
  ) => {
    if (severity === Severity.ERROR) trackAnalytics("dashboard_notification", { error_category: "request_failed" });
    let newToast: Toast = {
      id: Date.now() + 5000,
      description,
      severity,
      descriptionTranslation: translation,
      onRevert,
    };

    setToasts((ts) => {
      if (!!ts) {
        let filteredTs = ts.sort((a, b) => a.id - b.id);

        if (ts.length > 2) {
          filteredTs = filteredTs.slice(ts.length - 3, ts.length);
        }

        return [...filteredTs, newToast];
      } else {
        return [newToast];
      }
    });
  };

  const lightReservation = products?.includes('light-reservation') ?? false;

  const hasSia = products?.includes('phone');

  const hasSiaV02 = apps?.includes('sia-phone-assistant');

  const [siaState] = useDocument<SiaState>(
    'sia-state',
    hasSiaV02 ? restaurantId : null,
  );

  const [textHelperState, initTextHelper] = useState<null | TextHelperState>(
    null,
  );

  //////////////////////////
  const [notificationsV03Open, setnotificationsV03Open] = useLocalStorageState(
    'notificationsV03Open',
    false,
  );

  const [supportOpen, setSupportOpen] = useLocalStorageState(
    'supportOpen',
    false,
  );

  const SevenDaysAgo = useMemo(() => Date.now() - 7 * 24 * 60 * 60000, []);

  const [notificationsV03] = useCollection<NotificationV03>(
    'notificationsV03',
    {
      sort: ['createdAt', 'desc'],
      filter: [
        ['restaurantId', '==', restaurantId],
        ['createdAt', '>=', SevenDaysAgo],
      ],
      limit: 100,
    },
  );

  const filteredNotifications = useMemo(
    () =>
      notificationsV03.data.filter(
        (n) =>
          n.status !== 'done' &&
          (!n.irrelevantAfter || n.irrelevantAfter < Date.now()) &&
          (!n.date || n.date >= dateHelper()),
      ),
    [notificationsV03.data],
  );

  const notificationCategories = useMemo(
    () =>
      notificationsV03.data.reduce(
        (acc, n) => {
          let category = n.icon || 'other';

          acc[category] = [...(acc[category] ?? []), n.id];

          return acc;
        },
        {
          optimizations: [],
          mailbox: [],
          weather: [],
          comment: [],
          addOnOrder: [],
          rating: [],
        } as Record<string, string[]>,
      ),
    [filteredNotifications],
  );

  return (
    <RestaurantContext.Provider
      value={{
        products,
        switchRestaurant,
        restaurantId,
        alert,
        pinAuth,
        newToast,
        restaurantName,
        restaurantEmail,
        restaurantPhone,
        lightReservation,
        notifications,
        notificationsLoading,
        getAllNotifications,
        markAllNotificationsAsSeen,
        markNotificationAsSeen,
        deleteNotification,
        subscriptions,
        hasSia,
        hasSiaV02,
        reservationSettings:
          reservationSettings.data ?? reservationSettingsDraft.data ?? null,
        search,
        setSearch,
        textHelperState,
        initTextHelper,
        apps,
        setapps,
        appsRestaurantId,
        securityPlus: securityPlusDoc.data,
        securityPlusLoaded,
        storedPins,
        storedPinSessions,
        filteredNotifications,
        notificationsV03Open,
        setnotificationsV03Open,
        supportOpen,
        setSupportOpen,
        notificationCategories,
        experimentalFlags,
        globalFlags,
        siaState,
        deleted,
        deletedAt,
        projectId,
        dashboardProductLayout,
        customDataCatalog,
      }}
    >
      <SignatureContextProvider restaurantId={restaurantId}>
      <SecurityActivityProvider
        restaurantId={restaurantId}
        onDeliveryTrouble={() =>
          newToast(
            'Das Aktivitätsprotokoll konnte noch nicht alle Einträge übertragen. Sie werden automatisch erneut gesendet.',
            Severity.WARNING,
            null,
          )
        }
      >
        <StylesProvider injectFirst>
          <Alert state={alertState} onClose={() => alert(null)} />
          <CouponChangeDialog />
          <PinAlert
            isOpen={!!pinAuthState}
            onSubmit={(signature, to = 120000) => {
              if (pinPrompt && pinPrompt.scope !== pinScope) {
                pinPrompt.prompt.onClose?.('scope_changed');
                setPinPrompt(null);
                return;
              }
              const session = { sessionId: randomId() };
              if (pinAuthState?.onSubmit) pinAuthState.onSubmit(signature, session);
              if (pinAuthState)
                setPinCache((cache) => {
                  const current = cache.scope === pinScope ? cache.pins : {};
                  const pins: PinCache['pins'] = {};

                  for (const pin in current) {
                    if (current[pin].expiresAt > Date.now()) {
                      pins[pin] = current[pin];
                    }
                  }

                  pins[signature] = { expiresAt: Date.now() + to, ...session };

                  return { scope: pinScope, pins };
                });
              pinAuth(null);
            }}
            onWrongPin={(initials) => pinAuthState?.onWrongPin?.(initials)}
            signatures={pinAuthState?.signatures}
            onClose={() => {
              if (pinAuthState?.onClose) pinAuthState.onClose?.();
              pinAuth(null);
            }}
          />
          {!!textHelperState && restaurantId && (
            <TextHelper
              state={textHelperState}
              onClose={() => initTextHelper(null)}
              restaurantId={restaurantId}
            />
          )}
          {!experimentalFlags.data?.hideToasts && (
            <Toasts
              toasts={toasts || []}
              onClose={(id) =>
                setToasts((ts) => ts?.filter((t) => t.id !== id) || null)
              }
            />
          )}
          <ExperimentalFlagsModal />
        </StylesProvider>
        {children}
      </SecurityActivityProvider>
      </SignatureContextProvider>
    </RestaurantContext.Provider>
  );
};

export default RestaurantContextProvider;

export const useAddOnOrders = (restaurantId: string, date: string) => {
  const [addOnOrders] = useCollection<AddOnOrder>('addOnOrders', {
    filter: [
      ['restaurantId', '==', restaurantId],
      ['reservation.date', '==', date],
    ],
  });

  const addOnOrderMap = React.useMemo(() => {
    let map: Record<string, AddOnOrder[]> = {};

    addOnOrders?.data?.forEach((o) => {
      map[o.reservationId] = [...(map[o.reservationId] ?? []), o];
    });

    return map;
  }, [addOnOrders.data, restaurantId, date]);

  React.useEffect(() => {
    (window as any).addOnOrders = addOnOrders;
    (window as any).addOnOrderMap = addOnOrderMap;

    if (addOnOrders.error) {
      console.error('---- Add On Error ----', addOnOrders.error);
    }
  }, [addOnOrders]);

  const getAddOnOrders = React.useCallback(
    (id: string, all = false) => {
      if (all) {
        return addOnOrderMap[id] ?? [];
      } else {
        return (
          addOnOrderMap[id]?.filter(
            (o) => o.status !== 'failed' && o.status !== 'missing_payments',
          ) ?? []
        );
      }
    },
    [addOnOrderMap],
  );

  return {
    addOnOrders: addOnOrders?.data ?? [],
    addOnOrderMap,
    getAddOnOrders,
  };
};
